import React, { createContext, useState, ReactNode, useContext, useEffect } from "react";
import { Toast } from "../components/Toast";

interface ToastState {
    visible: boolean;
    message: string;
    mode: "success" | "error" | "warning" | "info";
}

interface ToastContextType {
    showToast: (message: any, mode: "success" | "error" | "warning" | "info") => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

    const [toast, setToast] = useState<ToastState>({
        visible: false,
        message: "",
        mode: "info",
    });

    const showToast = (message: any, mode: "success" | "error" | "warning" | "info") => {
        const safeMessage = typeof message === "string"
            ? message
            : message?.message || JSON.stringify(message) || "Something went wrong";
        setToast({ visible: true, message: safeMessage, mode });
    };

    const hideToast = () => {
        setToast((prev) => ({ ...prev, visible: false }));
    };

    useEffect(() => {
        if (toast.visible) {
            const timer = setTimeout(() => {
                hideToast();
            }, 5000); // Auto-close after 5 seconds
            return () => clearTimeout(timer);
        }
    }, [toast.visible, hideToast]);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            {toast.visible && (
                <Toast
                    message={toast.message}
                    mode={toast.mode}
                    onClose={hideToast}
                />
            )}
        </ToastContext.Provider>
    )
}

export const useToast = () => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};